import { useEffect } from "react";
import { HandHeart, Users, Globe, Sparkles } from "lucide-react";
import PageHeroBackground from "@/components/PageHeroBackground";

const IMPACT = [
  {
    Icon: Users,
    title: "Ministry & Discipleship",
    body: "Your giving keeps the lights on and the doors open — so the next person can hear the Gospel.",
  },
  {
    Icon: Globe,
    title: "Local & Global Outreach",
    body: "From DeLand to the nations, your gifts send the Gospel further than we could ever go alone.",
  },
  {
    Icon: Sparkles,
    title: "Lives Changed",
    body: "Every gift plays a part in someone's story — freedom, healing, and salvation in Jesus Christ.",
  },
];

export default function Give() {
  // Inject Donorbox widget script once when the page mounts
  useEffect(() => {
    const id = "donorbox-widget-script";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.src = "https://donorbox.org/widget.js";
    s.setAttribute("paypalExpress", "false");
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div data-testid="give-page">
      {/* HERO */}
      <section className="relative pt-40 pb-20 bg-[#FAFAF7] overflow-hidden" data-testid="give-hero">
        <PageHeroBackground opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-6 font-semibold">Give · Generosity</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#0B1D2E] leading-[1.05] tracking-tight">
              Give toward
              <span className="block italic text-[#123F33]">what God is doing.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#4A5562] leading-relaxed max-w-2xl">
              Giving at The Potter's House is an act of worship and a way to join God's work in DeLand and beyond. No pressure — just an invitation.
            </p>
          </div>
        </div>
      </section>

      {/* DONORBOX EMBED */}
      <section className="bg-white py-24" data-testid="give-donorbox-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="bg-[#F3F2EB] rounded-3xl border border-black/5 p-6 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-full bg-[#123F33]/10 flex items-center justify-center">
                <HandHeart className="w-5 h-5 text-[#123F33]" />
              </div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#123F33] font-semibold">Give Securely</div>
            </div>

            <div className="w-full flex justify-center" data-testid="donorbox-container">
              <iframe
                src="https://donorbox.org/embed/giving-82"
                name="donorbox"
                title="The Potter's House — Donorbox giving form"
                allow="payment"
                seamless="seamless"
                frameBorder="0"
                scrolling="no"
                height="900px"
                width="100%"
                style={{
                  maxWidth: "500px",
                  minWidth: "250px",
                  maxHeight: "none",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="bg-[#FAFAF7] py-28" data-testid="give-impact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-4 font-semibold">Your Impact</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B1D2E] leading-tight">
              Where your giving <span className="italic">goes.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {IMPACT.map(({ Icon, title, body }, i) => (
              <div
                key={i}
                data-testid={`impact-${i}`}
                className="bg-white rounded-2xl p-10 border border-black/5 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-full bg-[#123F33]/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-[#123F33]" />
                </div>
                <h3 className="font-serif text-2xl text-[#0B1D2E] mb-3">{title}</h3>
                <p className="text-[#4A5562] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCRIPTURE */}
      <section className="bg-[#0B1D2E] py-24" data-testid="give-scripture-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <blockquote className="font-serif text-3xl sm:text-4xl text-white italic leading-tight">
            "Each one should give what he has decided in his heart to give — for God loves a cheerful giver."
          </blockquote>
          <div className="mt-6 text-sm tracking-[0.22em] uppercase text-[#E4E1D8]">— 2 Corinthians 9:7</div>
        </div>
      </section>
    </div>
  );
}
